import React, { useState, useEffect, Component, ErrorInfo } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, Outlet } from 'react-router-dom';
import { Sun, Moon, LogOut, ShoppingCart, Package, Barcode, Truck, Users, CreditCard, DollarSign, Banknote, ShoppingBag, TrendingUp, Lock, Settings, LayoutDashboard, Menu, X } from 'lucide-react';
import { useAuth } from './contexts/AuthContext';
import Dashboard from './pages/Dashboard';
import Vendas from './pages/Vendas';
import Produtos from './pages/Produtos';
import Estoque from './pages/Estoque';
import Compras from './pages/Compras';
import Fornecedores from './pages/Fornecedores';
import Clientes from './pages/Clientes';
import Fiado from './pages/Fiado';
import Caixa from './pages/Caixa';
import Despesas from './pages/Despesas';
import Relatorios from './pages/Relatorios';
import Usuarios from './pages/Usuarios';
import Configuracoes from './pages/Configuracoes';

class ErrorBoundary extends Component<{ children: React.ReactNode }, { hasError: boolean; error: Error | null }> {
  state = { hasError: false, error: null };
  static getDerivedStateFromError(error: Error) { return { hasError: true, error }; }
  componentDidCatch(error: Error, errorInfo: ErrorInfo) { console.error('Erro capturado pelo ErrorBoundary:', error, errorInfo); }
  render() {
    if (this.state.hasError) {
      return (
        <div className="p-6 bg-red-50 border border-red-200 rounded-xl">
          <h2 className="text-xl font-bold text-red-900 mb-2">Erro na página</h2>
          <pre className="text-sm text-red-700 overflow-auto max-h-96">{this.state.error?.message ?? 'Erro desconhecido'}</pre>
          <button onClick={() => this.setState({ hasError: false, error: null })} className="mt-4 btn-primary">Tentar novamente</button>
        </div>
      );
    }
    return this.props.children;
  }
}

const MENU: { label: string; path: string; icon: React.ElementType }[] = [  { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
  { label: 'Vendas / PDV', path: '/vendas', icon: ShoppingCart },
  { label: 'Produtos', path: '/produtos', icon: Package },
  { label: 'Estoque', path: '/estoque', icon: Barcode },
  { label: 'Compras', path: '/compras', icon: Truck },
  { label: 'Fornecedores', path: '/fornecedores', icon: Users },
  { label: 'Clientes', path: '/clientes', icon: CreditCard },
  { label: 'Fiado / Devedores', path: '/fiado', icon: DollarSign },
  { label: 'Caixa', path: '/caixa', icon: Banknote },
  { label: 'Despesas', path: '/despesas', icon: ShoppingBag },
  { label: 'Relatórios', path: '/relatorios', icon: TrendingUp },
  { label: 'Usuários', path: '/usuarios', icon: Lock },
  { label: 'Configurações', path: '/configuracoes', icon: Settings },
];

function Navbar({ user, logout, toggleTheme, theme }: { user: any; logout: () => void; toggleTheme: () => void; theme: string }) {
  return (
    <nav className="sticky top-0 z-40 border-b border-gray-200 bg-white/80 backdrop-blur-md">
      <div className="flex items-center justify-between px-5 py-3">
        <div className="flex items-center gap-3">
          <button className="md:hidden text-gray-600 hover:text-brand-900" onClick={toggleTheme} title="Menu" aria-label="Abrir menu">
            <Menu size={22} />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-brand-900 flex items-center justify-center text-white font-bold text-xs">TL</div>
            <span className="font-bold text-brand-900 tracking-tight">TiaLeila1</span>
            <span className="text-xs text-gray-500 hidden sm:block">· Sistema de Vendas</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500 hidden sm:block uppercase tracking-wider">{user?.name}</span>
          <button className="btn-ghost hidden sm:flex items-center gap-2 text-sm" onClick={logout}>
            <LogOut size={16} /> Sair
          </button>
        </div>
      </div>
    </nav>
  );
}

function Sidebar({ open, toggle, user }: { open: boolean; toggle: () => void; user: any }) {
  return (
    <>
      <div className="fixed inset-0 z-20 bg-gray-900/60 md:hidden" onClick={toggle} />
      <aside className={`fixed inset-y-0 left-0 z-30 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ${open ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 md:relative md:static md:border-r md:z-0`}>
        <div className="flex items-center justify-between px-5 py-5 border-b border-gray-100">
          <button className="md:hidden text-gray-600" onClick={toggle} aria-label="Fechar menu">
            <X size={20} />
          </button>
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Menu</span>
        </div>
        <div className="py-3 space-y-1">
          {MENU.map((item) => {
            const Icon = item.icon;
            return (
              <Link key={item.path} to={item.path} className="flex items-center gap-3 px-5 py-3 text-sm font-medium rounded-lg transition hover:bg-gray-50" end>
                <Icon size={18} className="text-gray-500" />
                {item.label}
              </Link>
            );
          })}
        </div>
        <div className="absolute bottom-0 w-full border-t border-gray-100 p-5">
          <div className="flex items-center gap-3 px-2">
            <div className="w-8 h-8 rounded-full bg-gray-200 overflow-hidden">
              <div className="w-full h-full flex items-center justify-center text-xs font-bold text-gray-700">{user?.name?.[0]}</div>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">{user?.name}</p>
              <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}

const Login: React.FC = () => {
  const { setAuth, setLoading, isLoading } = useAuth();
  const [email, setEmail] = useState('admin@tialeila1.local');
  const [password, setPassword] = useState('tenderbr0');
  const [err, setErr] = useState('');
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true); setErr('');
    try {
      const r = await fetch(`${import.meta.env.VITE_API_URL || 'https://tialeila1.amadorzona.com/api'}/auth/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (!r.ok) throw new Error('Email ou senha inválidos');
      const json = await r.json();
      setAuth(json.token, json.user);
    } catch (e: any) { setErr(e.message || 'Erro ao fazer login'); }
    finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-brand-900 flex items-center justify-center text-white font-bold text-2xl mx-auto mb-3">TL</div>
          <h2 className="text-2xl font-bold text-brand-900">Tia Leila 1</h2>
          <p className="text-gray-600 text-sm">Sistema de Vendas de Bebidas</p>
        </div>
        <div className="card">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">E-mail</label>
              <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Senha</label>
              <input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            {err && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{err}</div>}
            <button className="btn-primary w-full" disabled={isLoading}>{isLoading ? 'Entrando...' : 'Entrar'}</button>
          </form>
          <p className="mt-5 text-xs text-center text-gray-500">admin@tialeila1.local · tenderbr0</p>
        </div>
      </div>
    </div>
  );
};

function Layout({ children }: { children: React.ReactNode }) {
  const { token, user, logout } = useAuth();
  const [theme, setTheme] = useState<'light'|'dark'>('light');
  const [sidebar, setSidebar] = useState(false);
  useEffect(() => {
    const saved = localStorage.getItem('tialeila_theme') as 'light'|'dark' | null;
    if (saved) setTheme(saved);
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const onChange = () => setSidebar(false);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  if (!token) return <Login />;
  return (
    <BrowserRouter>
      <Sidebar open={sidebar} toggle={() => setSidebar(!sidebar)} user={user} />
      <Navbar user={user} logout={() => { logout(); window.location.href = '/'; }} toggleTheme={() => { const t = theme === 'light' ? 'dark' : 'light'; setTheme(t); localStorage.setItem('tialeila_theme', t); }} theme={theme} />
      <div className={`transition-margin md:ml-64 min-h-screen py-6 px-4 sm:px-6 lg:px-8`}>
        <div className="max-w-7xl mx-auto">{children}</div>
      </div>
    </BrowserRouter>
  );
}

function ProtectedRoute() {
  const { token } = useAuth();
  if (!token) return <Navigate to="/login" replace />;
  return <Outlet />;
}

export default function App() {
  const { token } = useAuth();
  const [globalError, setGlobalError] = useState<{ message: string; stack?: string } | null>(null);

  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      setGlobalError({ message: event.message, stack: event.error?.stack });
    };
    const handleRejection = (event: PromiseRejectionEvent) => {
      setGlobalError({ message: String(event.reason), stack: event.reason?.stack });
    };
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

  if (globalError) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-xl min-h-screen flex flex-col items-center justify-center text-center">
        <h2 className="text-xl font-bold text-red-900 mb-2">Erro no aplicativo</h2>
        <pre className="text-sm text-red-700 text-left max-w-2xl overflow-auto">{globalError.message}</pre>
        {globalError.stack && (
          <div className="mt-2 text-xs text-gray-500">{globalError.stack}</div>
        )}
        <button onClick={() => setGlobalError(null)} className="mt-4 btn-primary">Recarregar página</button>
      </div>
    );
  }

  return (
    <>
      {!token ? <Login /> : (
        <Layout>
          <Routes>
            <Route path="/login" element={<Navigate to="/dashboard" replace />} />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route element={<ProtectedRoute />}>
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="vendas" element={<Vendas />} />
              <Route path="produtos" element={<Produtos />} />
              <Route path="estoque" element={<Estoque />} />
              <Route path="compras" element={<Compras />} />
              <Route path="fornecedores" element={<Fornecedores />} />
              <Route path="clientes" element={<Clientes />} />
              <Route path="fiado" element={<Fiado />} />
              <Route path="caixa" element={<Caixa />} />
              <Route path="despesas" element={<Despesas />} />
              <Route path="relatorios" element={<Relatorios />} />
              <Route path="usuarios" element={<Usuarios />} />
              <Route path="configuracoes" element={<Configuracoes />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Route>
          </Routes>
        </Layout>
      )}
    </>
  );
}
